import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, FileText, Download, Trash2, Loader2, Plus, Lock, Users, Building2, FileCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { downloadDocument } from "@/lib/download";
import { formatDate } from "@/lib/format";

const CATEGORIES = ["SAFE", "Term Sheet", "Cap Table", "Board Deck", "Financials", "Report", "Legal", "Other"];

function fmtSize(n) {
  if (!n) return "";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)} KB`;
  return `${n} B`;
}

const VIS_META = {
  all_investors: { label: "All investors", icon: Users, cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  specific: { label: "Specific", icon: Users, cls: "border-brand/30 bg-brand/10 text-brand-ink" },
  founder_only: { label: "Private", icon: Lock, cls: "border-slate-200 bg-slate-100 text-slate-700" },
  shared_with_founder: { label: "Shared with founder", icon: Building2, cls: "border-amber-200 bg-amber-50 text-amber-800" },
};

export const DocumentsPanel = ({ companyId, investors = [] }) => {
  const { user } = useAuth();
  const isFounder = user?.role === "founder";
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("Other");
  const [visibility, setVisibility] = useState("all_investors");
  const [sharedIds, setSharedIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");
  const fileRef = useRef();

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try { const { data } = await api.get(`/companies/${companyId}/documents`); setDocs(data); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [companyId]);

  const submit = async () => {
    if (!file) return toast.error("Choose a file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("visibility", isFounder ? visibility : "shared_with_founder");
      fd.append("shared_investor_ids", sharedIds.join(","));
      await api.post(`/companies/${companyId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      setOpen(false); setFile(null); setCategory("Other"); setVisibility("all_investors"); setSharedIds([]);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success("Document removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const dl = async (d) => { try { await downloadDocument(d.id, d.name); } catch { toast.error("Download failed"); } };

  const filtered = filter === "all" ? docs : docs.filter((d) => d.category === filter);
  const toggleShared = (id) => setSharedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="w-44">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="documents-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="upload-document-button"><Plus className="h-4 w-4" /> Upload document</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>File</Label>
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md,.png,.jpg,.jpeg,.gif,.webp,.zip,.key,.numbers,.pages" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="document-file-input" />
                <div className="mt-1 flex items-center gap-2">
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="gap-2" data-testid="choose-file-button"><Upload className="h-4 w-4" /> Choose file</Button>
                  <span className="truncate text-sm text-muted-foreground">{file ? file.name : "No file selected"}</span>
                </div>
                <p className="mt-1.5 text-xs text-muted-foreground" data-testid="upload-type-hint">PDF, Office files, images &amp; spreadsheets · max 25MB. HTML and SVG files aren't allowed.</p>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="mt-1" data-testid="document-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {isFounder && (
                <div>
                  <Label>Who can see this?</Label>
                  <Select value={visibility} onValueChange={setVisibility}>
                    <SelectTrigger className="mt-1" data-testid="document-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_investors">All investors</SelectItem>
                      <SelectItem value="specific">Specific investors</SelectItem>
                      <SelectItem value="founder_only">Only me (private)</SelectItem>
                    </SelectContent>
                  </Select>
                  {visibility === "specific" && (
                    <div className="mt-2 max-h-40 space-y-1 overflow-y-auto rounded-lg border p-2">
                      {investors.length === 0 ? <p className="p-1 text-xs text-muted-foreground">No investors yet.</p> : investors.map((inv) => (
                        <label key={inv.id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-secondary">
                          <Checkbox checked={sharedIds.includes(inv.id)} onCheckedChange={() => toggleShared(inv.id)} data-testid={`share-investor-${inv.id}`} />
                          {inv.name} <span className="text-xs text-muted-foreground">{inv.email}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!isFounder && <p className="text-xs text-muted-foreground">This document will be shared with the company's founder.</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="upload-cancel">Cancel</Button>
              <Button onClick={submit} disabled={uploading} className="gap-2" data-testid="upload-confirm">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading documents...</p>
      ) : filtered.length === 0 ? (
        <EmptyState icon={FileCheck} title="No documents yet" description="Upload SAFEs, term sheets, cap tables, board decks or financials to share securely." testId="empty-documents" />
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {filtered.map((d) => {
            const vm = VIS_META[d.visibility] || VIS_META.all_investors;
            const canDelete = isFounder || d.uploader_user_id === user?.id;
            return (
              <Card key={d.id} className="flex items-start gap-3 rounded-xl border bg-card p-4" data-testid={`document-item-${d.id}`}>
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><FileText className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-foreground">{d.name}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    <Badge variant="secondary" className="font-normal">{d.category}</Badge>
                    <Badge className={`gap-1 font-normal ${vm.cls}`}><vm.icon className="h-3 w-3" />{vm.label}</Badge>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">{fmtSize(d.size)} · {d.uploader_name} · {formatDate(d.created_at)}</div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => dl(d)} data-testid={`download-document-${d.id}`}><Download className="h-4 w-4" /></Button>
                  {canDelete && <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => remove(d.id)} data-testid={`delete-document-${d.id}`}><Trash2 className="h-4 w-4" /></Button>}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
